/**
 * WorkforceServiceCatalogue — the appointment catalogue, ONE document behind three
 * audiences (REQ-128, 2026-08-03).
 *
 * Riley (Operations admin) CONFIGURES: which lines of business a branch runs, which
 * services a branch switches off, and which line of business each advisor is tied to.
 * The branch manager and the advisor READ — same rows, same numbers, no controls
 * (operator ruling: "make those services available and viewable only, no configuration
 * change but provide a view into the service").
 *
 * The content is the operator's FinServ family tree: 7 lines of business → 24 intents →
 * 79 services. Three consequences for this screen:
 *  · Grouping is not decoration. 79 rows in a flat table is unusable, so the surface is
 *    LOB → intent → service with search across all three, and the intent level is
 *    LABELLED from the server (`intentLabel`), not hardcoded.
 *  · Pre/post time is real scheduling time. Each row shows duration and the TOTAL BLOCK
 *    it consumes (pre + duration + post), because that is what actually comes out of an
 *    advisor's day.
 *  · Booking windows are literal per row: where the source had no value the row says
 *    "not set" rather than inheriting a guess.
 *
 * Ops: getServiceCatalogue · setBranchLob · setBranchService · service/LOB/intent CRUD
 * (all awaited; the read-only audiences never see the write affordances).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Btn, DatePicker, FolderTree as UiFolderTree, Icon, Pill, T, alpha, type FolderTreeNode } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { useViewMode, ViewToggle, CARD_GRID } from "../../shell/list-composites";
import { useViewport } from "../../shell/useResponsive";
import { DataSourceStrip } from "../../shell/opSource";
import { Drawer, Modal } from "../../shell/Overlay";
import { toast } from "../../shell/toast";
import { useBusy } from "./useWorkforce";
import {
  getServiceCatalogue, setBranchLob, setBranchService, getMyServiceScope,
  createCatalogueService, updateCatalogueService, removeCatalogueService,
  createCatalogueLob, createCatalogueIntent, importCatalogueServices,
  type ServiceCatalogue, type CatalogueService, type ServiceDraft, type CatalogueQuestionGroup,
} from "./wf-api";
import { IMPORT_COLUMNS, importTemplateCsv, parseImportCsv, validateImportRows, downloadText, failureReportCsv, issueRowsCsv, type ParsedImport, type ImportValidation } from "./catalogue-import";
import { getUser } from "../../shell/access";
import { ServiceImageField, prepareServiceImage, type ServiceImageHandlers } from "../services/service-editor-controls";
import {
  uploadServiceImage, serviceImageTypeError, serviceImageSizeError, serviceImageBusy,
  SERVICE_IMAGE_IDLE, type ServiceImageUploadState,
} from "../services/services-data";
import { useT } from "../../lib/i18n";
import { useTenantLabels } from "../../lib/labels";

/* One request carries at most 200 rows; the platform refuses a larger import as a WHOLE, so a
 * 240-row file used to come back rejected with nothing imported and no row to point at. The count
 * is already in hand at stage time, so the file is stopped here with the number and the fix. */
const MAX_IMPORT_ROWS = 200;

/* Tenant vocabulary (2026-08-14, operator: "for [the university tenant] the By Branch should be By Department,
 * update that everywhere in the tenant"). The catalogue is the one screen that says "line of
 * business" and "branch" in a dozen places; both are BANK words. They resolve through the existing
 * per-tenant noun map (lib/labels) so a university reads "department" and "campus" — and the second
 * tab reads exactly what the operator asked for — with no per-tenant branching in the markup.
 * Lower-case forms are derived, not separate keys: a mid-sentence usage lowercases the tenant's
 * word itself, which is what lib/labels documents. */
function useVocab() {
  const label = useTenantLabels();
  const lob = label("serviceLine", "Line of business");
  const lobs = label("serviceLines", "Lines of business");
  const site = label("branch", "Branch");
  const sites = label("branches", "Branches");
  return {
    lob, lobs, site, sites,
    lobLower: lob.toLowerCase(), lobsLower: lobs.toLowerCase(),
    siteLower: site.toLowerCase(), sitesLower: sites.toLowerCase(),
    byTab: label("byBranchTab", "By branch"),
  };
}

const mono = { fontFamily: T.fontMono };
const card = { border: `1px solid ${T.border}`, borderRadius: 12, background: T.white, overflow: "hidden" as const, flexShrink: 0 };
const cap = { fontSize: 11, color: T.soft, textTransform: "uppercase" as const, letterSpacing: 0.05, fontWeight: 700 };
/* Import-history columns (operator, 2026-08-04): Status · Import name · Upload date/time ·
   Completion date/time · download. One constant shared by the header and the rows, so the two
   cannot drift apart; every flexible track carries a floor so the table never overflows its card. */
const IMPORT_COLS = "150px minmax(170px,1.4fr) minmax(160px,1fr) minmax(140px,1fr) 112px";

function channelLabel(c: CatalogueService["channels"], t: (k: string, f: string) => string, siteWord?: string): string {
  const parts: string[] = [];
  if (c.inPerson) parts.push(siteWord || t("workforce.catalogue.branch", "Branch"));
  if (c.phone) parts.push(t("workforce.catalogue.phone", "Phone"));
  if (c.video) parts.push(t("workforce.catalogue.virtual", "Virtual"));
  return parts.join(" · ") || t("workforce.catalogue.noChannel", "No channel set");
}

/** Duration + the pre/post shoulders, drawn to scale — the same visual language the
 *  day view uses: solid core, lighter shoulders. */
function BlockBar({ s }: { s: CatalogueService }) {
  const total = s.blockMin || (s.durationMin ?? 0);
  if (!total) return null;
  const pct = (n: number) => `${Math.max(0, (n / total) * 100)}%`;
  return (
    <span title={`${s.preMin} + ${s.durationMin} + ${s.postMin}`} style={{ display: "flex", width: 96, height: 8, borderRadius: 4, overflow: "hidden", background: T.n6 }}>
      {s.preMin ? <span style={{ width: pct(s.preMin), background: alpha(T.p1, 28) }} /> : null}
      <span style={{ width: pct(s.durationMin ?? 0), background: T.p1 }} />
      {s.postMin ? <span style={{ width: pct(s.postMin), background: alpha(T.p1, 28) }} /> : null}
    </span>
  );
}

export function ServiceDetail({ svc, intentName, lobName, groupName, surveyName, couponName, onClose }: { svc: CatalogueService; intentName: string; lobName: string; groupName: (id: string | null) => string | null; surveyName: (id: string | null) => string | null; couponName: (id: string) => string; onClose: () => void }) {
  const t = useT();
  const v = useVocab();
  const row = (label: string, value: React.ReactNode) => (
    <div style={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: 12, padding: "11px 0", borderBottom: `1px solid ${T.n6}` }}>
      <span style={{ fontSize: 12, color: T.soft, fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 13, color: T.n1 }}>{value}</span>
    </div>
  );
  const notSet = <span style={{ color: T.soft }}>{t("workforce.catalogue.notSet", "not set")}</span>;
  const mods = svc.modules ?? { appointments: false, queueing: false, events: false };
  const modList = [
    mods.appointments ? t("workforce.catalogue.modAppointments", "Appointments") : null,
    mods.queueing ? t("workforce.catalogue.modQueueing", "Queueing") : null,
    mods.events ? t("workforce.catalogue.modEvents", "Events") : null,
  ].filter(Boolean).join(" · ");
  const qg = svc.questionGroups ?? { appointments: null, queueing: null, events: null };
  const qgRows = ([
    [mods.appointments, t("workforce.catalogue.modAppointments", "Appointments"), qg.appointments],
    [mods.queueing, t("workforce.catalogue.modQueueing", "Queueing"), qg.queueing],
    [mods.events, t("workforce.catalogue.modEvents", "Events"), qg.events],
  ] as const).filter(([on]) => on);
  const sv = svc.surveys ?? { appointments: null, queueing: null, events: null };
  const svRows = ([
    [mods.appointments, t("workforce.catalogue.modAppointments", "Appointments"), sv.appointments],
    [mods.queueing, t("workforce.catalogue.modQueueing", "Queueing"), sv.queueing],
    [mods.events, t("workforce.catalogue.modEvents", "Events"), sv.events],
  ] as const).filter(([on, , id]) => on && !!id);
  return (
    <Drawer panel="service" width={520} testid="wfcat-service-drawer" onClose={onClose} title={svc.name} sub={`${lobName} · ${intentName}`}>
      <div style={{ padding: "6px 22px 26px" }}>
        {/* The artwork a customer sees on the journey, at the top — it is the first thing they see
            too, so reviewing the service should start with it. */}
        {svc.imageUrl ? (
          <img src={svc.imageUrl} alt={svc.imageAlt || ""} data-testid="wfcat-detail-image"
            style={{ width: "100%", aspectRatio: "16 / 9", objectFit: "cover", borderRadius: 10, display: "block", margin: "10px 0 4px", background: T.n6 }} />
        ) : null}
        {/* Unavailability is stated first: it changes what everything below means. */}
        {svc.bookableNow === false ? (
          <div style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "11px 13px", margin: "8px 0 4px", borderRadius: 8, background: alpha(T.orange1, 8), border: `1px solid ${alpha(T.orange1, 30)}` }}>
            <Icon.info size={13} />
            <span style={{ fontSize: 12, color: T.n2, lineHeight: 1.5 }}>
              {svc.unavailableReason === "before-window"
                ? `${t("workforce.catalogue.notBookableUntil", "Not bookable until")} ${svc.disableBefore}. ${t("workforce.catalogue.hiddenFromCustomers", "Customers can't see it yet.")}`
                : svc.unavailableReason === "after-window"
                ? `${t("workforce.catalogue.noLongerBookableSince", "No longer bookable — ended")} ${svc.disableAfter}.`
                : t("workforce.catalogue.archivedShort", "Archived — not bookable.")}
            </span>
          </div>
        ) : null}
        {svc.description ? (
          <p style={{ fontSize: 13, color: T.n2, lineHeight: 1.6, margin: "10px 0 14px" }}>{svc.description}</p>
        ) : null}
        {row(v.lob, lobName)}
        {row(t("workforce.catalogue.intent", "Intent"), intentName)}
        {row(t("workforce.catalogue.appointmentLength", "Appointment length"), <span style={mono}>{svc.durationMin} {t("workforce.catalogue.min", "min")}</span>)}
        {row(t("workforce.catalogue.totalBlock", "Total block"), (
          <span style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={mono}>{svc.blockMin} {t("workforce.catalogue.min", "min")}</span>
            <BlockBar s={svc} />
            <span style={{ fontSize: 11.5, color: T.soft, ...mono }}>{svc.preMin} + {svc.durationMin} + {svc.postMin}</span>
          </span>
        ))}
        {row(t("workforce.catalogue.bookableVia", "Bookable via"), modList || notSet)}
        {row(t("workforce.catalogue.channels", "Channels"), channelLabel(svc.channels, t, v.site))}
        {qgRows.length ? row(t("workforce.catalogue.bookingQuestions", "Booking questions"), (
          <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {qgRows.map(([, label, id]) => (
              <span key={label} style={{ fontSize: 12.5 }}>
                <span style={{ color: T.soft }}>{label}: </span>
                {groupName(id) ?? <span style={{ color: T.soft }}>{t("workforce.catalogue.noQuestions", "No questions")}</span>}
              </span>
            ))}
          </span>
        )) : null}
        {/* Surveys and coupons read alongside the questions, for the same reason
            they are edited alongside them: an operator reviewing a service wants
            to know everything it carries, not only what it asks. */}
        {svRows.length ? row(t("workforce.catalogue.followUpSurvey", "Follow-up survey"), (
          <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {svRows.map(([, label, id]) => (
              <span key={label} style={{ fontSize: 12.5 }}>
                <span style={{ color: T.soft }}>{label}: </span>
                {surveyName(id) ?? <span style={{ color: T.soft }}>{t("workforce.catalogue.noSurvey", "No survey")}</span>}
              </span>
            ))}
          </span>
        )) : null}
        {(svc.coupons ?? []).length ? row(t("workforce.catalogue.coupons", "Coupons"), (
          <span style={{ fontSize: 12.5 }}>{(svc.coupons ?? []).map(couponName).join(" · ")}</span>
        )) : null}
        {(svc.promos ?? []).length ? row(t("workforce.catalogue.promotions", "Promotions"), (
          <span style={{ fontSize: 12.5 }}>{(svc.promos ?? []).map(couponName).join(" · ")}</span>
        )) : null}
        {/* Where it is offered reads next to how it is delivered, since a student
            choosing a campus is making the same kind of choice. */}
        {(svc.locationChoices ?? []).length ? row(t("workforce.catalogue.locations", "Locations"), (
          <span style={{ fontSize: 12.5 }}>
            {(svc.locations ?? []).length
              ? (svc.locations ?? []).join(" · ")
              : <span style={{ color: T.soft }}>{t("workforce.catalogue.locationsNoneShort", "Not offered anywhere")}</span>}
          </span>
        )) : null}
        {row(t("workforce.catalogue.bookedBy", "Who can book it"), svc.bookedBy === "advisor"
          ? t("workforce.catalogue.advisorOnly", "Advisors only")
          : t("workforce.catalogue.customerOrAdvisor", "Customers and advisors"))}
        {svc.disableBefore || svc.disableAfter ? row(t("workforce.catalogue.availableBetween", "Available between"), (
          <span style={mono}>{svc.disableBefore ?? "—"} → {svc.disableAfter ?? "—"}</span>
        )) : null}
        {row(t("workforce.catalogue.minAdvance", "Earliest booking"), svc.minAdvance ?? notSet)}
        {row(t("workforce.catalogue.maxAdvance", "Latest booking"), svc.maxAdvance ?? notSet)}
        {row(t("workforce.catalogue.minCancel", "Cancellation window"), svc.minCancel ?? notSet)}
        <p style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.55, marginTop: 16 }}>
          {t("workforce.catalogue.blockExplainer", "The total block is what comes out of an advisor's day: preparation time, the appointment itself, then wrap-up. Availability reserves the whole block, not just the appointment.")}
        </p>
      </div>
    </Drawer>
  );
}

/* ── Riley's service editor (2026-08-03) ──────────────────────────────────
 * Every catalogue field, in the order a person thinks about a service: what it is, how long
 * it takes (and therefore what it BLOCKS), how it can be delivered, and when it can be booked.
 * Seeded rows are edited through an overlay — the operator's document stays authoritative and
 * the drawer says so, so nobody believes they are rewriting the source file.
 */
const FIELD_LABEL = { fontSize: 11.5, fontWeight: 700, color: T.n2 } as const;
const INPUT = { height: 34, padding: "0 10px", borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 13, fontFamily: T.font, color: T.n1, width: "100%", boxSizing: "border-box" as const };

function TextField({ label, value, onChange, placeholder, hint, testid }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; hint?: string; testid: string;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span style={FIELD_LABEL}>{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} style={INPUT} />
      {hint ? <span style={{ fontSize: 11, color: T.soft }}>{hint}</span> : null}
    </label>
  );
}
function NumField({ label, value, onChange, suffix, testid }: {
  label: string; value: number; onChange: (n: number) => void; suffix?: string; testid: string;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span style={FIELD_LABEL}>{label}</span>
      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input type="number" min={0} step={5} value={String(value)} data-testid={testid}
          onChange={(e) => onChange(Math.max(0, parseInt(e.target.value, 10) || 0))} style={{ ...INPUT, width: 88 }} />
        {suffix ? <span style={{ fontSize: 12, color: T.soft }}>{suffix}</span> : null}
      </span>
    </label>
  );
}

/* Branch scope — a searchable dropdown (operator, 2026-08-03: "should be a drop down with search
 * functionality to find branches, not a pill list"). A bank has hundreds of branches; a chip row
 * only works for three. Whole-network is the first option, not a separate control. */
function BranchPicker({ branches, value, onChange }: { branches: string[]; value: string | null; onChange: (b: string | null) => void }) {
  const t = useT();
  const v = useVocab();
  const [open, setOpen] = useState(false); // ui-local
  const [q, setQ] = useState(""); // ui-local
  const all = t("workforce.catalogue.wholeNetwork", "Whole network");
  const hits = branches.filter((b) => b.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, position: "relative" }}>
      <span style={{ fontSize: 11.5, color: T.soft, fontWeight: 600 }}>{t("workforce.catalogue.viewAs", "Showing")}</span>
      <button type="button" data-testid="wfcat-scope" aria-haspopup="listbox" aria-expanded={open} onClick={() => { setOpen((o) => !o); setQ(""); }}
        style={{ display: "inline-flex", alignItems: "center", gap: 8, height: 32, padding: "0 11px", borderRadius: 8, border: `1px solid ${open ? T.p1 : T.border}`, background: T.white, color: T.n1, fontSize: 12.5, fontWeight: 600, fontFamily: T.font, cursor: "pointer", maxWidth: 240 }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value ?? all}</span>
        <Icon.chev size={12} />
      </button>
      {open ? (
        <>
          <span onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <span style={{ position: "absolute", top: 38, right: 0, zIndex: 41, width: 268, padding: 8, borderRadius: 10, border: `1px solid ${T.border}`, background: T.white, boxShadow: T.shadowLg, display: "flex", flexDirection: "column", gap: 6 }}>
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="wfcat-scope-search"
              placeholder={`${t("workforce.catalogue.searchVerb", "Search")} ${v.sitesLower}…`}
              style={{ height: 32, padding: "0 10px", borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12.5, fontFamily: T.font, color: T.n1 }} />
            <span role="listbox" style={{ display: "flex", flexDirection: "column", maxHeight: 260, overflow: "auto" }}>
              <button type="button" role="option" aria-selected={!value} data-testid="wfcat-scope-option" data-id="__all"
                onClick={() => { onChange(null); setOpen(false); }}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 10px", borderRadius: 6, border: "none", background: !value ? T.p3 : "transparent", color: !value ? T.p1 : T.n1, fontSize: 12.5, fontWeight: !value ? 700 : 500, fontFamily: T.font, textAlign: "left", cursor: "pointer" }}>
                {all}{!value ? <Icon.check size={12} /> : null}
              </button>
              {hits.length === 0 ? (
                <span style={{ padding: "10px", fontSize: 12, color: T.soft }}>{`${t("workforce.catalogue.noMatchFor", "No")} ${v.siteLower} ${t("workforce.catalogue.matchesThat", "matches that.")}`}</span>
              ) : hits.map((b) => (
                <button key={b} type="button" role="option" aria-selected={value === b} data-testid="wfcat-scope-option" data-id={b}
                  onClick={() => { onChange(b); setOpen(false); }}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 10px", borderRadius: 6, border: "none", background: value === b ? T.p3 : "transparent", color: value === b ? T.p1 : T.n1, fontSize: 12.5, fontWeight: value === b ? 700 : 500, fontFamily: T.font, textAlign: "left", cursor: "pointer" }}>
                  {b}{value === b ? <Icon.check size={12} /> : null}
                </button>
              ))}
            </span>
          </span>
        </>
      ) : null}
    </span>
  );
}

/* Per-LOB branch availability — a searchable multi-select per line of business, all branches
 * selected by default (operator, 2026-08-03). Replaces the chip grid, which did not scale and
 * made "everywhere" look like an exception rather than the norm. */
function LobBranchPicker({ lob, branches, selected, busy, onToggle, onAll, onNone }: {
  lob: { id: string; name: string }; branches: string[]; selected: Set<string>; busy: boolean;
  onToggle: (branch: string, on: boolean) => void; onAll: () => void; onNone: () => void;
}) {
  const t = useT();
  const v = useVocab();
  const [open, setOpen] = useState(false); // ui-local
  const [q, setQ] = useState(""); // ui-local
  const hits = branches.filter((b) => b.toLowerCase().includes(q.trim().toLowerCase()));
  const n = selected.size, total = branches.length;
  const label = n === total ? `${t("workforce.catalogue.allOf", "All")} ${v.sitesLower}` : n === 0 ? `${t("workforce.catalogue.noneOf", "No")} ${v.sitesLower}` : `${n} ${t("workforce.catalogue.ofBranches", "of")} ${total}`;
  return (
    <div data-testid="wfcat-lob-branches" data-id={lob.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "13px 20px", borderBottom: `1px solid ${T.n6}` }}>
      <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <span style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{lob.name}</span>
        <span style={{ fontSize: 11, color: n === 0 ? T.orange1 : T.soft }}>
          {n === 0 ? t("workforce.catalogue.notOfferedAnywhere", "Not offered anywhere — its services are unbookable") : n === total ? `${t("workforce.catalogue.offeredAtEvery", "Offered at every")} ${v.siteLower}` : [...selected].sort().join(" · ")}
        </span>
      </span>
      <span style={{ position: "relative", flexShrink: 0 }}>
        <button type="button" data-testid="wfcat-lob-branch-open" aria-haspopup="listbox" aria-expanded={open} disabled={busy}
          onClick={() => { setOpen((o) => !o); setQ(""); }}
          style={{ display: "inline-flex", alignItems: "center", gap: 8, height: 32, padding: "0 11px", borderRadius: 8, border: `1px solid ${open ? T.p1 : T.border}`, background: T.white, color: T.n1, fontSize: 12.5, fontWeight: 600, fontFamily: T.font, cursor: busy ? "default" : "pointer" }}>
          {busy ? "…" : label}
          <Icon.chev size={12} />
        </button>
        {open ? (
          <>
            <span onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
            <span style={{ position: "absolute", top: 38, right: 0, zIndex: 41, width: 280, padding: 8, borderRadius: 10, border: `1px solid ${T.border}`, background: T.white, boxShadow: T.shadowLg, display: "flex", flexDirection: "column", gap: 6 }}>
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="wfcat-lob-branch-search"
                placeholder={`${t("workforce.catalogue.searchVerb", "Search")} ${v.sitesLower}…`}
                style={{ height: 32, padding: "0 10px", borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12.5, fontFamily: T.font, color: T.n1 }} />
              <span style={{ display: "flex", gap: 6 }}>
                <button type="button" data-testid="wfcat-lob-branch-all" onClick={onAll}
                  style={{ flex: 1, padding: "5px 8px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.white, color: T.p1, fontSize: 11.5, fontWeight: 700, fontFamily: T.font, cursor: "pointer" }}>
                  {t("workforce.catalogue.selectAll", "Select all")}
                </button>
                <button type="button" data-testid="wfcat-lob-branch-none" onClick={onNone}
                  style={{ flex: 1, padding: "5px 8px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.white, color: T.n2, fontSize: 11.5, fontWeight: 600, fontFamily: T.font, cursor: "pointer" }}>
                  {t("workforce.catalogue.clearAll", "Clear")}
                </button>
              </span>
              <span role="listbox" aria-multiselectable style={{ display: "flex", flexDirection: "column", maxHeight: 240, overflow: "auto" }}>
                {hits.length === 0 ? (
                  <span style={{ padding: 10, fontSize: 12, color: T.soft }}>{`${t("workforce.catalogue.noMatchFor", "No")} ${v.siteLower} ${t("workforce.catalogue.matchesThat", "matches that.")}`}</span>
                ) : hits.map((b) => {
                  const on = selected.has(b);
                  return (
                    <button key={b} type="button" role="option" aria-selected={on} data-testid="wfcat-lob-branch-option" data-id={`${lob.id}|${b}`}
                      onClick={() => onToggle(b, !on)}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 6, border: "none", background: on ? T.p3 : "transparent", color: on ? T.p1 : T.n1, fontSize: 12.5, fontWeight: on ? 700 : 500, fontFamily: T.font, textAlign: "left", cursor: "pointer" }}>
                      <span style={{ width: 13, height: 13, borderRadius: 3, border: `1.5px solid ${on ? T.p1 : T.border}`, background: on ? T.p1 : T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {on ? <Icon.check size={9} color={T.white} /> : null}
                      </span>
                      {b}
                    </button>
                  );
                })}
              </span>
            </span>
          </>
        ) : null}
      </span>
    </div>
  );
}

/* Service artwork — what a customer sees on the journey (operator, 2026-08-03).
 *
 * A real upload, through the service-asset lifecycle the services editor already owns
 * (presign → signed PUT → confirm, `ServiceImageField` + `uploadServiceImage`). The field only
 * ever hands back a CONFIRMED hosted address.
 *
 * It used to read the file with FileReader and hand back a `data:` URL. That could never
 * succeed: this route stores a hosted https:// address and refuses a data: URL — and because
 * `save()` sends `imageUrl` on EVERY save, one refused image took the name, duration, channels
 * and question groups down with it. Nothing local reaches `imageUrl` now, so no edit can be
 * lost to the image field. */
function ImageField({ url, alt, onChange, onUploadingChange, serviceId, testid }: {
  url: string; alt: string; onChange: (next: { imageUrl: string; imageAlt: string }) => void;
  onUploadingChange: (uploading: boolean) => void; serviceId?: string | null; testid: string;
}) {
  const t = useT();
  const [upload, setUpload] = useState<ServiceImageUploadState>(SERVICE_IMAGE_IDLE);
  const runRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  /* Leaving the drawer mid-upload: stop the run so a late confirm cannot write into a closed
     editor. Whatever the server already made is left alone — a confirmed asset may be a
     deduplicated match another service points at. */
  useEffect(() => () => { runRef.current++; if (abortRef.current) abortRef.current.abort(); }, []);
  useEffect(() => { onUploadingChange(serviceImageBusy(upload)); }, [upload, onUploadingChange]);

  const cancel = () => {
    runRef.current++;
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;
  };
  const start = async (file: File) => {
    cancel();
    const run = runRef.current;
    const live = () => run === runRef.current;
    /* Type and size are judged on the ORIGINAL pick, before any prepare or presign, so an
       unusable file is refused having issued zero requests. */
    const typeError = serviceImageTypeError(file);
    if (typeError) { setUpload({ stage: "failed", error: typeError, file }); return; }
    const sizeError = serviceImageSizeError(file);
    if (sizeError) { setUpload({ stage: "failed", error: sizeError, file }); return; }
    const controller = typeof AbortController === "function" ? new AbortController() : null;
    abortRef.current = controller;
    setUpload({ stage: "preparing", error: "", file });
    try {
      const prepared = await prepareServiceImage(file);
      if (!live()) return;
      const confirmed = await uploadServiceImage({
        ...prepared,
        originalSize: file.size,
        altText: alt || null,
        serviceId: serviceId || null,
        signal: controller ? controller.signal : undefined,
        onStage: (stage, progress) => { if (live()) setUpload({ stage, progress, error: "", file }); },
      });
      if (!live()) return;
      onChange({ imageUrl: confirmed.url, imageAlt: alt || file.name.replace(/\.[^.]+$/, "") });
      setUpload({ stage: "saved", progress: 1, error: "", file });
    } catch (e) {
      if (!live()) return;
      const msg = String((e as { message?: string })?.message || "").trim();
      setUpload({ stage: "failed", file, error: msg || t("workforce.catalogue.imageUploadFailed", "That image couldn’t be uploaded, so the service image is unchanged.") });
    }
  };
  const handlers: ServiceImageHandlers = {
    upload,
    pick: (file) => { void start(file); },
    retry: () => { if (upload.file) void start(upload.file); },
    dismiss: () => { cancel(); setUpload(SERVICE_IMAGE_IDLE); },
    /* Remove DETACHES: it clears this service's reference and never deletes the asset. */
    remove: () => { cancel(); setUpload(SERVICE_IMAGE_IDLE); onChange({ imageUrl: "", imageAlt: "" }); },
    setUrl: (next) => { cancel(); setUpload(SERVICE_IMAGE_IDLE); onChange({ imageUrl: next, imageAlt: alt }); },
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }} data-testid={testid}>
      <span style={{ ...cap, fontSize: 10 }}>{t("workforce.catalogue.serviceImage", "Image shown on the journey")}</span>
      <ServiceImageField image={url} assetId={null} handlers={handlers} />
      <input value={alt} onChange={(e) => onChange({ imageUrl: url, imageAlt: e.target.value })}
        placeholder={t("workforce.catalogue.altPlaceholder", "Describe the image, for screen readers")}
        data-testid="wfcat-f-imagealt" style={INPUT} />
      {url && !alt ? (
        <span style={{ fontSize: 11, color: T.orange1 }}>{t("workforce.catalogue.altMissing", "Add a description — without it the image is invisible to anyone using a screen reader.")}</span>
      ) : (
        <span style={{ fontSize: 11, color: T.soft }}>{t("workforce.catalogue.imageHint", "Shown wherever a customer picks this service — online booking, the lobby kiosk and event listings. Landscape works best.")}</span>
      )}
    </div>
  );
}

function ServiceEditor({ svc, catalogue, busy, onSave, onArchive, onRestore, onDelete, onClose }: {
  svc: CatalogueService | null;            // null = adding
  catalogue: {
    lobs: { id: string; name: string }[];
    intents: { id: string; name: string; lob: string }[];
    questionGroups: CatalogueQuestionGroup[];
    surveys?: { id: string; name: string; questions?: number }[];
    coupons?: { id: string; code?: string; name?: string; type?: string; kind?: string; value?: number; label?: string; status?: string }[];
  };
  busy: string | null;
  /* `locations` rides ALONGSIDE the draft rather than inside it, because a
     service has no locations[] to save: where it is offered is the branch
     model's business, written with setBranchService. The editor hands back the
     ticked set and the caller diffs it. */
  onSave: (draft: ServiceDraft, id: string | null, locations?: string[]) => void;
  onArchive: ((s: CatalogueService) => void) | null;
  onRestore: ((s: CatalogueService) => void) | null;
  onDelete: ((s: CatalogueService) => void) | null;
  onClose: () => void;
}) {
  const t = useT();
  const v = useVocab();
  /* Where it is offered. Derived server-side and sent as two lists: what may be
     ticked, and what is. A NEW service has neither until it exists and has a
     line of business, so the field hides rather than showing an empty box. */
  const locChoices = svc?.locationChoices ?? [];
  const [locs, setLocs] = useState<string[]>(svc?.locations ?? []);
  /* Defend EVERY catalogue array here, at the boundary, rather than trusting each caller: this
     component crashed the whole Services screen once because one new array (questionGroups) was
     missing from the shape a caller passed. Local aliases make that impossible to repeat. */
  const lobOptions = catalogue.lobs ?? [];
  const intentOptions = catalogue.intents ?? [];
  const groupOptions = catalogue.questionGroups ?? [];
  const surveyOptions = catalogue.surveys ?? [];
  /* One library, two kinds: a COUPON is a code the customer enters, a PROMO
     applies automatically. The promos library types each row, so the split is a
     filter here rather than two reads. */
  const discountOptions = catalogue.coupons ?? [];
  const couponOptions = discountOptions.filter((d) => d.type !== "promo");
  const promoOptions = discountOptions.filter((d) => d.type === "promo");
  const added = !!svc && svc.id.startsWith("sw_svc_new_");
  const [name, setName] = useState(svc?.name ?? "");
  const [description, setDescription] = useState(svc?.description ?? "");
  const [imageUrl, setImageUrl] = useState(svc?.imageUrl ?? "");
  const [imageAlt, setImageAlt] = useState(svc?.imageAlt ?? "");
  /* Saving mid-upload would persist the state the field is about to replace, so the save waits
     for the confirm rather than racing it. */
  const [imageUploading, setImageUploading] = useState(false);
  const [lob, setLobId] = useState(svc?.lob ?? lobOptions[0]?.id ?? "");
  const [intent, setIntent] = useState(svc?.intent ?? "");
  const [durationMin, setDuration] = useState(svc?.durationMin ?? 30);
  const [preMin, setPre] = useState(svc?.preMin ?? 0);
  const [postMin, setPost] = useState(svc?.postMin ?? 0);
  const [inPerson, setInPerson] = useState(svc?.channels.inPerson ?? true);
  const [phone, setPhone] = useState(svc?.channels.phone ?? false);
  const [video, setVideo] = useState(svc?.channels.video ?? false);
  const [minAdvance, setMinAdv] = useState(svc?.minAdvance ?? "");
  const [maxAdvance, setMaxAdv] = useState(svc?.maxAdvance ?? "");
  const [minCancel, setMinCancel] = useState(svc?.minCancel ?? "");
  const [bookedBy, setBookedBy] = useState<"customer" | "advisor">(svc?.bookedBy ?? "customer");
  /* Booking modules — WHERE it can be booked (alongside the delivery channels above). */
  const [mAppt, setMAppt] = useState(svc?.modules?.appointments ?? true);
  const [mQueue, setMQueue] = useState(svc?.modules?.queueing ?? false);
  const [mEvent, setMEvent] = useState(svc?.modules?.events ?? false);
  /* ONE booking-question group per method (operator ruling). */
  const [qgAppt, setQgAppt] = useState(svc?.questionGroups?.appointments ?? "");
  const [qgQueue, setQgQueue] = useState(svc?.questionGroups?.queueing ?? "");
  const [qgEvent, setQgEvent] = useState(svc?.questionGroups?.events ?? "");
  /* Surveys mirror the questions exactly — per method, for the same reason. */
  const [svAppt, setSvAppt] = useState(svc?.surveys?.appointments ?? "");
  const [svQueue, setSvQueue] = useState(svc?.surveys?.queueing ?? "");
  const [svEvent, setSvEvent] = useState(svc?.surveys?.events ?? "");
  /* Coupons are NOT per method: a discount belongs to the thing being bought,
     and the same offer holds however the customer reached it. */
  const [coupons, setCoupons] = useState<string[]>(svc?.coupons ?? []);
  const [autoPromos, setAutoPromos] = useState<string[]>(svc?.promos ?? []);
  const [disableBefore, setDisableBefore] = useState(svc?.disableBefore ?? "");
  const [disableAfter, setDisableAfter] = useState(svc?.disableAfter ?? "");

  const intentsForLob = intentOptions.filter((i) => i.lob === lob);
  /* Changing the line of business invalidates the intent: intents belong to one LOB. */
  const pickLob = (id: string) => { setLobId(id); setIntent((cur) => (intentOptions.some((i) => i.id === cur && i.lob === id) ? cur : "")); };
  const valid = name.trim() && lob && intent;
  const block = { preMin, durationMin, postMin };

  const save = () => {
    if (imageUploading) {
      toast({ tone: "warn", title: t("workforce.catalogue.imageStillUploading", "The image is still uploading"), subtitle: t("workforce.catalogue.waitThenSave", "Wait for it to finish, then save.") });
      return;
    }
    onSave({
    name: name.trim(), lob, intent, durationMin, preMin, postMin,
    description: description.trim() || null,
    channels: { inPerson, phone, video },
    modules: { appointments: mAppt, queueing: mQueue, events: mEvent },
    questionGroups: { appointments: mAppt ? (qgAppt || null) : null, queueing: mQueue ? (qgQueue || null) : null, events: mEvent ? (qgEvent || null) : null },
    /* Cleared with the method, exactly as the question group is: switching
       Queueing off must not leave a survey attached to a method this service no
       longer offers. */
    surveys: { appointments: mAppt ? (svAppt || null) : null, queueing: mQueue ? (svQueue || null) : null, events: mEvent ? (svEvent || null) : null },
    coupons,
    promos: autoPromos,
    disableBefore: disableBefore || null, disableAfter: disableAfter || null,
    minAdvance: minAdvance.trim() || null, maxAdvance: maxAdvance.trim() || null, minCancel: minCancel.trim() || null,
    bookedBy,
    imageUrl: imageUrl.trim() || null, imageAlt: imageAlt.trim() || null,
    }, svc?.id ?? null, svc ? locs : undefined);
  };

  const asDate = (iso: string) => { const [y, m, d] = iso.split("-").map(Number); return new Date(y, (m || 1) - 1, d || 1); };
  const dpPick = (v: string | null, apply: (d: string) => void) => apply(v && v.length >= 10 ? v.slice(0, 10) : "");
  const groupSelect = (value: string, onChange: (v: string) => void, testid: string) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} style={INPUT}>
      <option value="">{t("workforce.catalogue.noQuestions", "No questions")}</option>
      {(groupOptions).map((g) => <option key={g.id} value={g.id}>{g.name} ({g.questions})</option>)}
    </select>
  );
  const surveySelect = (value: string, onChange: (v: string) => void, testid: string) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} style={INPUT}>
      <option value="">{t("workforce.catalogue.noSurvey", "No survey")}</option>
      {surveyOptions.map((sv) => <option key={sv.id} value={sv.id}>{sv.name}{sv.questions ? ` (${sv.questions})` : ""}</option>)}
    </select>
  );

  return (
    <Drawer panel="service-edit" width={560} testid="wfcat-editor" onClose={onClose}
      title={svc ? t("workforce.catalogue.editService", "Edit service") : t("workforce.catalogue.addService", "Add a service")}
      sub={svc ? svc.name : t("workforce.catalogue.addSub", "It appears in the catalog as soon as you save it")}>
      <div style={{ padding: "16px 22px 26px", display: "flex", flexDirection: "column", gap: 18 }}>
        {svc && !added ? (
          <div style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "11px 13px", borderRadius: 8, background: svc.archived ? alpha(T.orange1, 8) : alpha(T.n1, 3), border: `1px solid ${svc.archived ? alpha(T.orange1, 30) : T.n6}` }}>
            <Icon.info size={13} />
            <span style={{ fontSize: 11.5, color: T.n2, lineHeight: 1.5 }}>
              {svc.archived
                ? t("workforce.catalogue.archivedNote", "This service is archived: it is not bookable and does not appear in any advisor's list. Restore it to put it back in the catalog.")
                : t("workforce.catalogue.seededNote", "This service came from the seeded catalog. Your changes are kept as an override — the original definition stays intact underneath.")}
            </span>
          </div>
        ) : null}

        <TextField label={t("workforce.catalogue.serviceName", "Service name")} value={name} onChange={setName}
          placeholder={t("workforce.catalogue.namePlaceholder", "What a customer books")} testid="wfcat-f-name" />

        <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <span style={FIELD_LABEL}>{t("workforce.catalogue.descriptionOptional", "Description (optional)")}</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
            data-testid="wfcat-f-description" placeholder={t("workforce.catalogue.descriptionPlaceholder", "What the customer gets, in their words")}
            style={{ ...INPUT, height: "auto", padding: "8px 10px", resize: "vertical", lineHeight: 1.5 }} />
        </label>

        <ImageField url={imageUrl} alt={imageAlt} serviceId={svc?.id ?? null}
          onUploadingChange={setImageUploading}
          onChange={(next) => { setImageUrl(next.imageUrl); setImageAlt(next.imageAlt); }} testid="wfcat-f-image" />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <span style={FIELD_LABEL}>{v.lob}</span>
            <select value={lob} onChange={(e) => pickLob(e.target.value)} data-testid="wfcat-f-lob" style={INPUT}>
              {lobOptions.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <span style={FIELD_LABEL}>{t("workforce.catalogue.intent", "Intent")}</span>
            <select value={intent} onChange={(e) => setIntent(e.target.value)} data-testid="wfcat-f-intent" style={INPUT}>
              <option value="">{t("workforce.catalogue.pickAnIntent", "Pick an intent…")}</option>
              {intentsForLob.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </label>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <span style={{ ...cap, fontSize: 10 }}>{t("workforce.catalogue.timeItTakes", "Time it takes")}</span>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            <NumField label={t("workforce.catalogue.prep", "Prep")} value={preMin} onChange={setPre} suffix={t("workforce.catalogue.min", "min")} testid="wfcat-f-pre" />
            <NumField label={t("workforce.catalogue.appointment", "Appointment")} value={durationMin} onChange={setDuration} suffix={t("workforce.catalogue.min", "min")} testid="wfcat-f-duration" />
            <NumField label={t("workforce.catalogue.wrapUp", "Wrap-up")} value={postMin} onChange={setPost} suffix={t("workforce.catalogue.min", "min")} testid="wfcat-f-post" />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 13px", borderRadius: 8, background: alpha(T.p1, 4), border: `1px solid ${alpha(T.p1, 18)}` }}>
            <BlockBar s={{ ...(svc ?? ({} as CatalogueService)), ...block, blockMin: preMin + durationMin + postMin } as CatalogueService} />
            <span style={{ fontSize: 12.5, fontWeight: 700, color: T.n1, ...mono }}>{preMin + durationMin + postMin} {t("workforce.catalogue.min", "min")}</span>
            <span style={{ fontSize: 11.5, color: T.soft }}>{t("workforce.catalogue.blockedInTheDay", "blocked in an advisor's day")}</span>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span style={{ ...cap, fontSize: 10 }}>{t("workforce.catalogue.howItCanBeDelivered", "How it can be delivered")}</span>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {([[inPerson, setInPerson, v.site, "inperson"], [phone, setPhone, t("workforce.catalogue.phone", "Phone"), "phone"], [video, setVideo, t("workforce.catalogue.virtual", "Virtual"), "video"]] as const).map(([on, set, label, key]) => (
              <button key={key} type="button" data-testid="wfcat-f-channel" data-id={key} aria-pressed={on} onClick={() => set(!on)}
                style={{ padding: "6px 13px", borderRadius: 999, border: `1px solid ${on ? T.p1 : T.border}`, background: on ? alpha(T.p1, 10) : T.white, color: on ? T.p1 : T.n2, fontSize: 12.5, fontWeight: on ? 700 : 600, fontFamily: T.font, cursor: "pointer" }}>
                {label}
              </button>
            ))}
          </div>
          {!inPerson && !phone && !video ? (
            <span style={{ fontSize: 11, color: T.orange1, fontWeight: 600 }}>{t("workforce.catalogue.noChannelWarning", "With no channel selected, nobody can book this.")}</span>
          ) : null}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span style={{ ...cap, fontSize: 10 }}>{t("workforce.catalogue.whereItCanBeBooked", "Where it can be booked")}</span>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {([[mAppt, setMAppt, t("workforce.catalogue.modAppointments", "Appointments"), "appointments"], [mQueue, setMQueue, t("workforce.catalogue.modQueueing", "Queueing"), "queueing"], [mEvent, setMEvent, t("workforce.catalogue.modEvents", "Events"), "events"]] as const).map(([on, set, label, key]) => (
              <button key={key} type="button" data-testid="wfcat-f-module" data-id={key} aria-pressed={on} onClick={() => set(!on)}
                style={{ padding: "6px 13px", borderRadius: 999, border: `1px solid ${on ? T.p1 : T.border}`, background: on ? alpha(T.p1, 10) : T.white, color: on ? T.p1 : T.n2, fontSize: 12.5, fontWeight: on ? 700 : 600, fontFamily: T.font, cursor: "pointer" }}>
                {label}
              </button>
            ))}
          </div>
          {!mAppt && !mQueue && !mEvent ? (
            <span style={{ fontSize: 11, color: T.orange1, fontWeight: 600 }}>{t("workforce.catalogue.noModuleWarning", "With no booking method selected, nobody can book this.")}</span>
          ) : null}
          <span style={{ fontSize: 11, color: T.soft }}>{t("workforce.catalogue.modulesHint", "Queueing and Events use the same length and prep/wrap-up as the appointment.")}</span>
        </div>

        {/* ONE booking-question group per method (operator ruling): a kiosk queue asks less than
            a booked appointment, so the group is chosen per method rather than per service. */}
        {mAppt || mQueue || mEvent ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <span style={{ ...cap, fontSize: 10 }}>{t("workforce.catalogue.bookingQuestions", "Booking questions")}</span>
            {mAppt ? (
              <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <span style={FIELD_LABEL}>{t("workforce.catalogue.modAppointments", "Appointments")}</span>
                {groupSelect(qgAppt, setQgAppt, "wfcat-f-qg-appointments")}
              </label>
            ) : null}
            {mQueue ? (
              <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <span style={FIELD_LABEL}>{t("workforce.catalogue.modQueueing", "Queueing")}</span>
                {groupSelect(qgQueue, setQgQueue, "wfcat-f-qg-queueing")}
              </label>
            ) : null}
            {mEvent ? (
              <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <span style={FIELD_LABEL}>{t("workforce.catalogue.modEvents", "Events")}</span>
                {groupSelect(qgEvent, setQgEvent, "wfcat-f-qg-events")}
              </label>
            ) : null}
            <span style={{ fontSize: 11, color: T.soft }}>{t("workforce.catalogue.questionsHint", "Groups are maintained in Booking questions. A service takes one group per method.")}</span>
          </div>
        ) : null}

        {/* Follow-up survey — per method, mirroring the questions above and for
            the same reason. Moved here from the journey (operator ruling
            2026-09-02): a journey could hold ONE survey for every service it
            offered, which was never how a survey is actually chosen. */}
        {mAppt || mQueue || mEvent ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <span style={{ ...cap, fontSize: 10 }}>{t("workforce.catalogue.followUpSurvey", "Follow-up survey")}</span>
            {mAppt ? (
              <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <span style={FIELD_LABEL}>{t("workforce.catalogue.modAppointments", "Appointments")}</span>
                {surveySelect(svAppt, setSvAppt, "wfcat-f-sv-appointments")}
              </label>
            ) : null}
            {mQueue ? (
              <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <span style={FIELD_LABEL}>{t("workforce.catalogue.modQueueing", "Queueing")}</span>
                {surveySelect(svQueue, setSvQueue, "wfcat-f-sv-queueing")}
              </label>
            ) : null}
            {mEvent ? (
              <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <span style={FIELD_LABEL}>{t("workforce.catalogue.modEvents", "Events")}</span>
                {surveySelect(svEvent, setSvEvent, "wfcat-f-sv-events")}
              </label>
            ) : null}
            <span style={{ fontSize: 11, color: T.soft }}>
              {surveyOptions.length === 0
                ? t("workforce.catalogue.surveysNoneYet", "No surveys in the library yet — create one in Surveys and it will appear here.")
                : t("workforce.catalogue.surveysHint", "Sent after the visit. Surveys are maintained in the Surveys library.")}
            </span>
          </div>
        ) : null}

        {/* WHERE it is offered (operator, 2026-09-08). Not a field on the
            service: this writes through setBranchService, the op that already
            governs per-branch availability. Only campuses that CARRY this
            service's line of business are offered — the operator's ruling, so a
            tick can never quietly enable a line somewhere it is not run. */}
        {svc && locChoices.length ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span style={{ ...cap, fontSize: 10 }}>{t("workforce.catalogue.locations", "Locations")}</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {locChoices.map((b) => {
                const on = locs.includes(b);
                return (
                  <button
                    key={b}
                    type="button"
                    data-testid={`wfcat-f-loc-${b}`}
                    aria-pressed={on}
                    onClick={() => setLocs(on ? locs.filter((x) => x !== b) : [...locs, b])}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 999,
                      border: `1px solid ${on ? T.p1 : T.border}`, background: on ? T.p3 : T.white,
                      color: on ? T.p1 : T.n2, fontSize: 11.5, fontWeight: on ? 700 : 600,
                      cursor: "pointer", fontFamily: "inherit",
                    }}>
                    <span aria-hidden="true">{on ? "✓" : "+"}</span>{b}
                  </button>
                );
              })}
            </div>
            <span style={{ fontSize: 11, color: T.soft }}>
              {locs.length === 0
                ? t("workforce.catalogue.locationsNone", "Not offered anywhere — students will not find this service at any campus.")
                : t("workforce.catalogue.locationsHint", "Campuses running this line of business. Untick one to stop offering the service there.")}
            </span>
          </div>
        ) : null}

        {/* Coupons and promotions — two different incentives, so two fields.
            A COUPON is a code the customer enters; a PROMO applies automatically.
            Neither is per method: a discount is a property of the thing being
            bought, and the same offer holds however the customer reached it.
            Lists, because several can apply at once. */}
        {([
          { key: "coupons" as const, label: t("workforce.catalogue.coupons", "Coupons"), rows: couponOptions, value: coupons, set: setCoupons,
            empty: t("workforce.catalogue.couponsNoneYet", "No coupons in the library yet — create one in Coupons and it will appear here."),
            hint: t("workforce.catalogue.couponsHint", "Codes the customer enters. Accepted on this service however it is reached.") },
          { key: "promos" as const, label: t("workforce.catalogue.promotions", "Promotions"), rows: promoOptions, value: autoPromos, set: setAutoPromos,
            empty: t("workforce.catalogue.promosNoneYet", "No automatic promotions in the library yet."),
            hint: t("workforce.catalogue.promosHint", "Applied automatically, with no code to enter.") },
        ]).map((grp) => (
          <div key={grp.key} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span style={{ ...cap, fontSize: 10 }}>{grp.label}</span>
            {grp.rows.length === 0 ? (
              <span style={{ fontSize: 11, color: T.soft }}>{grp.empty}</span>
            ) : (
              <>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {grp.rows.map((c) => {
                    const on = grp.value.includes(c.id);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        data-testid={`wfcat-f-${grp.key}-${c.id}`}
                        onClick={() => grp.set(on ? grp.value.filter((x) => x !== c.id) : [...grp.value, c.id])}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 999,
                          /* `T.p6` and `T.text` are not tokens (2026-09-08). The
                             primary ramp is p1–p4, and `T.text` is the TYPE-role
                             map (an object of font sizes), not a colour — using
                             it as one set `color` to "[object Object]". `p3` is
                             the tinted primary surface `toneColors("primary")`
                             itself uses, and `n2` is the body ink. */
                          border: `1px solid ${on ? T.p1 : T.border}`, background: on ? T.p3 : T.white,
                          /* Pressed INK is p1, the ramp's dark end — not p2. The
                             comment above records the previous token fix here;
                             its replacement was wrong the same way, because p2
                             (#DCCCFC) and p3 (#EDE5FE) are neighbouring TINTS,
                             so "✓ PREPSENIOR" rendered at 1.22:1 and the only
                             signal that a coupon is attached was unreadable.
                             p1 on p3 is what the bookedBy pills below already
                             use for their selected state. */
                          color: on ? T.p1 : T.n2, fontSize: 11.5, fontWeight: on ? 700 : 600, cursor: "pointer", fontFamily: "inherit",
                        }}
                      >
                        <span style={{ fontSize: 10 }}>{on ? "✓" : "+"}</span>
                        {/* A coupon is known by its code; a promo has none, so it
                            is known by its name. */}
                        {grp.key === "coupons" ? (c.code || c.name || c.id) : (c.name || c.id)}
                        {c.kind && c.value != null ? <span style={{ color: T.soft, fontWeight: 500 }}>· {c.kind === "percent" ? `${c.value}%` : `${c.value} off`}</span> : null}
                      </button>
                    );
                  })}
                </div>
                <span style={{ fontSize: 11, color: T.soft }}>{grp.hint}</span>
              </>
            )}
          </div>
        ))}

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <span style={{ ...cap, fontSize: 10 }}>{t("workforce.catalogue.availableBetween", "Available between")}</span>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <span style={FIELD_LABEL}>{t("workforce.catalogue.notBookableBefore", "Not bookable before")}</span>
              <DatePicker size="sm" full showTime={false} allowPast includeDow={false} testid="wfcat-f-before"
                value={disableBefore ? `${disableBefore}T00:00` : ""}
                maxDate={disableAfter ? asDate(disableAfter) : undefined}
                onChange={(v) => dpPick(v, setDisableBefore)} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <span style={FIELD_LABEL}>{t("workforce.catalogue.notBookableAfter", "Not bookable after")}</span>
              <DatePicker size="sm" full showTime={false} allowPast includeDow={false} testid="wfcat-f-after"
                value={disableAfter ? `${disableAfter}T00:00` : ""}
                minDate={disableBefore ? asDate(disableBefore) : undefined}
                onChange={(v) => dpPick(v, setDisableAfter)} />
            </label>
          </div>
          <span style={{ fontSize: 11, color: T.soft, lineHeight: 1.5 }}>
            {t("workforce.catalogue.windowExplainer", "Outside these dates the service disappears from customer booking, and staff see it greyed out with the date. Leave both empty for always available.")}
          </span>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <span style={{ ...cap, fontSize: 10 }}>{t("workforce.catalogue.bookingWindow", "Booking window")}</span>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            <TextField label={t("workforce.catalogue.minAdvance", "Earliest booking")} value={minAdvance} onChange={setMinAdv} placeholder={t("workforce.catalogue.egHours", "e.g. 2 hours")} testid="wfcat-f-minadv" />
            <TextField label={t("workforce.catalogue.maxAdvance", "Latest booking")} value={maxAdvance} onChange={setMaxAdv} placeholder={t("workforce.catalogue.egWeeks", "e.g. 8 weeks")} testid="wfcat-f-maxadv" />
            <TextField label={t("workforce.catalogue.minCancel", "Cancellation window")} value={minCancel} onChange={setMinCancel} placeholder={t("workforce.catalogue.egHours2", "e.g. 4 hours")} testid="wfcat-f-mincancel" />
          </div>
          <span style={{ fontSize: 11, color: T.soft }}>{t("workforce.catalogue.windowHint", "Leave a field empty and the catalog shows it as not set, rather than guessing a default.")}</span>
          {/* WHO may initiate a booking — the one axis the module pills do not cover. "Customers"
              implies advisors too: an advisor can always book what a customer can. */}
          <span style={{ ...cap, fontSize: 10, paddingTop: 2 }}>{t("workforce.catalogue.bookedBy", "Who can book it")}</span>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {([["customer", t("workforce.catalogue.customerOrAdvisor", "Customers and advisors")], ["advisor", t("workforce.catalogue.advisorOnly", "Advisors only")]] as const).map(([id, label]) => {
              const on = bookedBy === id;
              return (
                <button key={id} type="button" data-testid="wfcat-f-bookedby" data-id={id} aria-pressed={on} onClick={() => setBookedBy(id)}
                  style={{ padding: "6px 13px", borderRadius: 999, border: `1px solid ${on ? T.p1 : T.border}`, background: on ? alpha(T.p1, 10) : T.white, color: on ? T.p1 : T.n2, fontSize: 12.5, fontWeight: on ? 700 : 600, fontFamily: T.font, cursor: "pointer" }}>
                  {label}
                </button>
              );
            })}
          </div>
          <span style={{ fontSize: 11, color: T.soft }}>
            {bookedBy === "advisor"
              ? t("workforce.catalogue.advisorOnlyHint", "Customers can't book this themselves — an advisor schedules it, typically after qualifying them.")
              : t("workforce.catalogue.customerHint", "Customers can book it themselves, and advisors can book it for them.")}
          </span>
        </div>

        <div style={{ display: "flex", gap: 9, alignItems: "center", paddingTop: 4, flexWrap: "wrap" }}>
          <Btn disabled={!valid || busy !== null} testid="wfcat-save" onClick={save}>
            {busy === "save" ? t("workforce.catalogue.saving", "Saving…") : svc ? t("workforce.catalogue.saveChanges", "Save changes") : t("workforce.catalogue.addToCatalogue", "Add to catalog")}
          </Btn>
          <Btn variant="secondary" onClick={onClose}>{t("common.actions.cancel", "Cancel")}</Btn>
          {svc && onArchive ? (
            /* An archived row offers RESTORE, not a second archive. Before this the button read
               "Archived", stayed enabled and re-sent the same archive — a silent no-op. */
            svc.archived && !added ? (
              <Btn variant="secondary" disabled={busy !== null} testid="wfcat-restore" onClick={() => onRestore && onRestore(svc)}>
                {busy === "save" ? t("workforce.catalogue.saving", "Saving…") : t("workforce.catalogue.restore", "Restore to the catalog")}
              </Btn>
            ) : (
              <Btn variant="secondary" disabled={busy !== null} testid="wfcat-archive" onClick={() => onArchive(svc)}>
                {t("workforce.catalogue.archive", "Archive")}
              </Btn>
            )
          ) : null}
          {/* DELETE is for live environments and never touches the seeded catalogue (operator
              ruling): only a row Riley added can be deleted, and it asks first. */}
          {svc && added && onDelete ? (
            <Btn variant="secondary" disabled={busy !== null} testid="wfcat-delete" onClick={() => onDelete(svc)}>
              {t("workforce.catalogue.delete", "Delete…")}
            </Btn>
          ) : null}
          {!valid ? <span style={{ fontSize: 11.5, color: T.soft }}>{`${t("workforce.catalogue.needName", "Name")}, ${v.lobLower} ${t("workforce.catalogue.andIntentRequired", "and intent are required.")}`}</span> : null}
        </div>
      </div>
    </Drawer>
  );
}

export default function WorkforceServiceCatalogue({ mode = "config", branch = null, rail = "appt-services" }: {
  /** "config" = Riley. "view" = branch manager / advisor: same rows, no controls. */
  mode?: "config" | "view";
  branch?: string | null;
  rail?: string;
}) {
  const t = useT();
  const v = useVocab();
  const [data, setData] = useState<ServiceCatalogue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState(""); // ui-local
  /* Folder scope (2026-09-02). The LOB → intent tree IS this catalogue's taxonomy — two levels,
     server-authored — so it is presented with the design system's FolderTree, the same folder
     metaphor every other library surface uses, instead of a pill row that could only ever show
     the top level. One scope id: "all", a LOB id, or an intent id. */
  const [fscope, setFscope] = useState<string>("all"); // ui-local
  const [openLobs, setOpenLobs] = useState<string[]>([]); // ui-local (tree expansion)
  const [view, setView] = useViewMode("wfcatalogue:view");
  const desktop = useViewport() === "desktop";
  const [scope, setScope] = useState<string | null>(branch); // ui-local (branch scope)
  const [tab, setTab] = useState<"services" | "branches" | "import">("services"); // ui-local
  const [staged, setStaged] = useState<(ParsedImport & { fileName: string; fileSize: number; fileText: string; uploadedAt: string; check: ImportValidation }) | null>(null); // ui-local
  const [openRun, setOpenRun] = useState<string | null>(null); // ui-local (which run's failures are expanded)
  const [detail, setDetail] = useState<CatalogueService | null>(null); // ui-local
  /* Editing state (Riley only). `editing` holds the row being edited; "new" means the add
     form. Kept separate from `detail` so the read-only audiences can never reach it. */
  const [editing, setEditing] = useState<CatalogueService | "new" | null>(null); // ui-local
  const [confirmDelete, setConfirmDelete] = useState<CatalogueService | null>(null); // ui-local
  const [newLob, setNewLob] = useState(""); // ui-local
  const [newIntentFor, setNewIntentFor] = useState<string | null>(null); // ui-local (LOB id)
  const [newIntent, setNewIntent] = useState(""); // ui-local
  const { busy, run } = useBusy();
  const config = mode === "config";

  useEffect(() => {
    let live = true;
    setLoading(true); setError(null);
    /* Two audiences, two ops — deliberately. The advisor and the branch manager read the
       SELF-scoped op (`/v1/me/workforce/services`), which is read-only by construction and
       already scoped to their branch and line of business; only Riley reads the admin
       catalogue. Adapted into one shape so the rendering below is identical for both. */
    const p = config
      ? getServiceCatalogue(scope)
      : getMyServiceScope().then((m): ServiceCatalogue => ({
          lobs: m.lobs ?? [], intents: m.intents ?? [], categories: m.intents ?? [],
          /* The read-only audiences see everything their BRANCH offers, not only their own line:
             the branch manager is reading the catalogue, and an advisor needs to know what else
             the branch can do so he knows what to refer on (2026-08-03). */
          services: m.branchServices ?? m.services ?? [], advisors: [], roleLobs: {},
          questionGroups: m.questionGroups ?? [],
          branches: [], branchLobs: {}, scopeBranch: m.locationName, intentLabel: "Intent",
        }));
    p.then((r) => { if (live) { setData(r); setLoading(false); } })
      .catch((e: unknown) => { if (live) { setError(e instanceof Error ? e.message : String(e)); setLoading(false); } });
    return () => { live = false; };
  }, [scope, config]);

  const lobName = (id: string) => data?.lobs.find((l) => l.id === id)?.name ?? id;
  const intentName = (id: string) => data?.intents.find((i) => i.id === id)?.name ?? id;
  const groupName = (id: string | null) => (id ? (data?.questionGroups ?? []).find((g) => g.id === id)?.name ?? id : null);
  /* Falls back to the id rather than to "unknown": an id on screen is something
     an operator can search for, and it says plainly that the record it names has
     gone missing rather than that the field is empty. */
  const surveyName = (id: string | null) => (id ? (data?.surveys ?? []).find((x) => x.id === id)?.name ?? id : null);
  /* A coupon is known by its code, a promo has none and is known by its name.
     Reading `code` alone printed an empty string for every automatic promo. */
  const couponName = (id: string) => {
    const d = (data?.coupons ?? []).find((c) => c.id === id);
    return d ? (d.code || d.name || id) : id;
  };

  /* Every array is defended: the legacy ops/services layer once answered this screen with a
     shape that carried no `intents`, and an undefined .filter() took the whole screen down. */
  const safe = data ? {
    lobs: data.lobs ?? [], intents: data.intents ?? [], services: data.services ?? [],
    advisors: data.advisors ?? [], branches: data.branches ?? [], branchLobs: data.branchLobs ?? {},
    questionGroups: data.questionGroups ?? [],
    surveys: data.surveys ?? [],
    coupons: data.coupons ?? [],
    intentLabel: data.intentLabel || "Intent",
  } : null;

  /* LOB → intent → services, filtered. Search covers all three levels so "notary",
     "borrow" and "wealth" all find something. */
  const scopeIntent = safe?.intents.find((i) => i.id === fscope) ?? null;
  const lob = scopeIntent ? scopeIntent.lob : (safe?.lobs.some((l) => l.id === fscope) ? fscope : null);
  const intentSel = scopeIntent?.id ?? null;
  const groups = useMemo(() => {
    if (!safe) return [];
    const needle = q.trim().toLowerCase();
    return safe.lobs
      .filter((l) => !lob || l.id === lob)
      .map((l) => {
        const intents = safe.intents.filter((i) => i.lob === l.id && (!intentSel || i.id === intentSel)).map((i) => {
          const svcs = safe.services.filter((s) => {
            if (s.intent !== i.id) return false;
            if (!needle) return true;
            return [s.name, i.name, l.name].join(" ").toLowerCase().includes(needle);
          });
          return { intent: i, services: svcs };
        }).filter((g) => g.services.length);
        return { lob: l, intents, count: intents.reduce((a, g) => a + g.services.length, 0) };
      })
      .filter((g) => g.count > 0);
  }, [safe, q, lob, intentSel]);

  /* Tree nodes. Counts follow the search so the tree reports where the hits are, but no node is
     ever removed: a folder that vanishes mid-search takes the user's scope with it. */
  const treeNodes = useMemo<FolderTreeNode[]>(() => {
    if (!safe) return [];
    const needle = q.trim().toLowerCase();
    const hit = (s: CatalogueService, iName: string, lName: string) =>
      !needle || [s.name, iName, lName].join(" ").toLowerCase().includes(needle);
    let all = 0;
    const nodes = safe.lobs.map((l) => {
      const children = safe.intents.filter((i) => i.lob === l.id).map((i) => ({
        id: i.id,
        label: i.name,
        count: safe.services.filter((s) => s.intent === i.id && hit(s, i.name, l.name)).length,
      }));
      const count = children.reduce((a, c) => a + (c.count ?? 0), 0);
      all += count;
      return { id: l.id, label: l.name, count, children };
    });
    return [{ id: "all", label: `${t("workforce.catalogue.allOf", "All")} ${v.lobsLower}`, count: all }, ...nodes];
  }, [safe, q, t, v.lobsLower]);

  /* Flat, scope-respecting list for the card view. */
  const flatServices = useMemo(
    () => groups.flatMap((g) => g.intents.flatMap((ig) => ig.services.map((s) => ({ s, intent: ig.intent, lob: g.lob })))),
    [groups],
  );

  const total = safe?.services.length ?? 0;
  const shown = groups.reduce((a, g) => a + g.count, 0);

  const toggleBranchLob = (b: string, id: string, enabled: boolean) => void run(`bl:${b}:${id}`, async () => {
    try { const r = await setBranchLob(b, id, enabled); setData(r); }
    catch (e) { toast({ tone: "danger", title: t("workforce.catalogue.couldntSave", "Couldn't save that"), subtitle: e instanceof Error ? e.message : String(e) }); }
  });
  const toggleBranchSvc = (b: string, id: string, enabled: boolean) => void run(`bs:${b}:${id}`, async () => {
    try { const r = await setBranchService(b, id, enabled); setData(r); }
    catch (e) { toast({ tone: "danger", title: t("workforce.catalogue.couldntSave", "Couldn't save that"), subtitle: e instanceof Error ? e.message : String(e) }); }
  });

  /* Service CRUD. Every write returns the whole catalogue, so the screen adopts the server's
     state rather than patching its own copy — which is what keeps the row, the drawer and the
     advisor's read in agreement. */
  const saveService = (draft: ServiceDraft, id: string | null, locations?: string[]) => void run("save", async () => {
    try {
      let r: ServiceCatalogue = id ? await updateCatalogueService(id, draft) : await createCatalogueService(draft);
      /* Locations are not part of the draft — where a service is offered belongs
         to the branch model, so each change is a setBranchService call. Diffed
         against what the row already had so an unchanged tick writes nothing,
         and sequential because every config write returns the WHOLE catalogue:
         firing them in parallel would race and the last response home would win
         with the others' changes missing. The final response is the one kept. */
      if (id && locations) {
        const before = new Set(editing !== "new" && editing ? (editing.locations ?? []) : []);
        const after = new Set(locations);
        const changed = [...new Set([...before, ...after])].filter((b) => before.has(b) !== after.has(b));
        for (const b of changed) r = await setBranchService(b, id, after.has(b));
      }
      setData(r);
      setEditing(null);
      toast({ tone: "success", title: id ? t("workforce.catalogue.serviceSaved", "Service updated") : t("workforce.catalogue.serviceAdded", "Service added"), subtitle: draft.name });
    } catch (e) {
      toast({ tone: "danger", title: t("workforce.catalogue.couldntSave", "Couldn't save that"), subtitle: e instanceof Error ? e.message : String(e) });
    }
  });
  const archiveService = (s: CatalogueService) => void run("save", async () => {
    try {
      const r = await removeCatalogueService(s.id);
      setData(r);
      setEditing(null);
      toast({ tone: "success", title: r.removed ? t("workforce.catalogue.serviceRemoved", "Service removed") : t("workforce.catalogue.serviceArchived", "Service archived"), subtitle: s.name });
    } catch (e) {
      toast({ tone: "danger", title: t("workforce.catalogue.couldntSave", "Couldn't save that"), subtitle: e instanceof Error ? e.message : String(e) });
    }
  });
  /* Restore is the same PATCH the editor uses for any other field — archiving is a flag, not a
     destruction, so putting a service back needs no separate op. */
  const restoreService = (s: CatalogueService) => void run("save", async () => {
    try {
      const r = await updateCatalogueService(s.id, { archived: false });
      setData(r);
      setEditing(null);
      toast({ tone: "success", title: t("workforce.catalogue.serviceRestored", "Service restored"), subtitle: s.name });
    } catch (e) {
      toast({ tone: "danger", title: t("workforce.catalogue.couldntSave", "Couldn't save that"), subtitle: e instanceof Error ? e.message : String(e) });
    }
  });

  const deleteService = (s: CatalogueService) => void run("save", async () => {
    try {
      const r = await removeCatalogueService(s.id);
      setData(r);
      setConfirmDelete(null);
      setEditing(null);
      toast({ tone: "success", title: t("workforce.catalogue.serviceDeleted", "Service deleted"), subtitle: s.name });
    } catch (e) {
      toast({ tone: "danger", title: t("workforce.catalogue.couldntDelete", "Couldn't delete that"), subtitle: e instanceof Error ? e.message : String(e) });
    }
  });
  /* Riley extends the TREE, not just its leaves: a new line of business, or a new intent under
     one. Added rows are overlays, so the operator's seed document is never rewritten. */
  const addLob = () => void run("tree", async () => {
    try { const r = await createCatalogueLob({ name: newLob.trim() }); setData(r); setNewLob(""); toast({ tone: "success", title: `${v.lob} ${t("workforce.catalogue.addedWord", "added")}`, subtitle: newLob.trim() }); }
    catch (e) { toast({ tone: "danger", title: t("workforce.catalogue.couldntSave", "Couldn't save that"), subtitle: e instanceof Error ? e.message : String(e) }); }
  });
  const addIntent = (lobId: string) => void run("tree", async () => {
    try { const r = await createCatalogueIntent({ name: newIntent.trim(), lob: lobId }); setData(r); setNewIntent(""); setNewIntentFor(null); toast({ tone: "success", title: t("workforce.catalogue.intentAdded", "Intent added"), subtitle: newIntent.trim() }); }
    catch (e) { toast({ tone: "danger", title: t("workforce.catalogue.couldntSave", "Couldn't save that"), subtitle: e instanceof Error ? e.message : String(e) }); }
  });

  const doImport = () => {
    if (!staged) return;
    void run("import", async () => {
      try {
        const u = getUser();
        const r = await importCatalogueServices({
          rows: staged.check.validRows, by: u.name || "Unknown user",
          /* The PERSONA's own role, not the events-process assignment slot: Riley is an Operations
             admin whose events slot happens to read "System Administrator", and an audit trail
             must never record a higher-privilege role than the actor holds. */
          byRole: u.role || u.assignments?.events || null,
          fileName: staged.fileName, fileSize: staged.fileSize, fileText: staged.fileText, uploadedAt: staged.uploadedAt,
        });
        setData(r);
        setStaged(null);
        toast({
          tone: r.run.failed ? "warn" : "success",
          title: `${r.run.imported} ${t("workforce.catalogue.imported", "services imported")}`,
          subtitle: r.run.failed ? `${r.run.failed} ${t("workforce.catalogue.rowsFailed", "rows failed — see the history below")}` : staged.fileName,
        });
      } catch (e) {
        toast({ tone: "danger", title: t("workforce.catalogue.importFailed", "Import failed"), subtitle: e instanceof Error ? e.message : String(e) });
      }
    });
  };
  const stageFile = (file: File | null) => {
    if (!file) return;
    const fr = new FileReader();
    fr.onload = () => {
      const text = String(fr.result || "");
      const parsed = parseImportCsv(text);
      /* Validate NOW, against the catalogue this screen already holds, so every problem is visible
         while the operator can still fix the spreadsheet. The server re-checks on import. */
      const check = validateImportRows(parsed.rows, {
        lobs: safe?.lobs ?? [], intents: safe?.intents ?? [],
        questionGroups: safe?.questionGroups ?? [],
        existingNames: (safe?.services ?? []).map((x) => x.name),
      });
      setStaged({ ...parsed, check, fileName: file.name, fileSize: file.size, fileText: text, uploadedAt: new Date().toISOString() });
    };
    fr.readAsText(file);
  };

  return (
    <Screen rail={rail}>
      <PageHeader
        title={config ? t("workforce.catalogue.title", "Services") : `${v.site} ${t("workforce.catalogue.servicesLower", "services")}`}
        subtitle={config
          ? `${t("workforce.catalogue.subtitleLead", "The appointment catalogue customers can book — by")} ${v.lobLower} ${t("workforce.catalogue.subtitleTail", "and intent, with the time each one takes.")}`
          : `${t("workforce.catalogue.subtitleSiteLead", "Everything this")} ${v.siteLower} ${t("workforce.catalogue.subtitleSiteTail", "offers and how long each appointment takes. Set up by Operations.")}`}
        breadcrumb={false} />
      <DataSourceStrip screen="wfcatalogue" ops={["getServiceCatalogue", "setBranchLob", "setBranchService", "createCatalogueService", "updateCatalogueService", "removeCatalogueService", "createCatalogueLob", "createCatalogueIntent"]} reqId="REQ-128" />

      {config ? (
        <div style={{ display: "flex", gap: 18, padding: "0 24px", borderBottom: `1px solid ${T.n6}`, flexShrink: 0 }}>
          {([["services", t("workforce.catalogue.tabServices", "Catalog")], ["branches", v.byTab], ["import", t("workforce.catalogue.tabImport", "Bulk import")]] as const).map(([id, label]) => (
            <button key={id} type="button" data-testid="wfcat-tab" data-id={id} onClick={() => setTab(id)}
              style={{ padding: "11px 2px", border: "none", background: "none", borderBottom: `2px solid ${tab === id ? T.p1 : "transparent"}`, color: tab === id ? T.p1 : T.n2, fontSize: 13, fontWeight: 700, fontFamily: T.font, cursor: "pointer" }}>
              {label}
            </button>
          ))}
        </div>
      ) : null}

      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 34px", display: "flex", flexDirection: "column", gap: 16 }}>
        {error ? <div style={{ padding: 40, textAlign: "center", color: T.soft }}>{t("workforce.catalogue.couldntLoad", "Couldn't load the catalog:")} {error}</div>
          : loading || !safe ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("common.status.loading", "Loading…")}</div>
          : tab === "services" ? (
            <div style={{ display: "flex", gap: 18, alignItems: "flex-start", minHeight: 0 }}>
              {/* Folder tree — the catalogue's LOB → intent taxonomy, in the design system's tree.
                  Left panel on desktop; folder cards below the filters on phone/tablet, which is
                  the same split the other library surfaces make. */}
              {desktop ? (
                <div style={{ width: 232, flexShrink: 0, borderRight: `1px solid ${T.border}`, paddingRight: 10, position: "sticky", top: 0, alignSelf: "flex-start" }}>
                  <UiFolderTree
                    nodes={treeNodes}
                    selectedId={fscope}
                    onSelect={setFscope}
                    expandedIds={openLobs}
                    onToggle={(id, open) => setOpenLobs((o) => (open ? [...o, id] : o.filter((x) => x !== id)))}
                    aria-label={v.lobs}
                    testid="wfcat-folders" />
                </div>
              ) : null}
              <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", flexShrink: 0 }}>
                <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="wfcat-search"
                  placeholder={`${t("workforce.catalogue.searchServicesIntents", "Search services, intents,")} ${v.lobsLower}…`}
                  style={{ height: 34, width: 320, padding: "0 12px", borderRadius: 8, border: `1px solid ${T.border}`, fontSize: 13, fontFamily: T.font, color: T.n1 }} />
                <span style={{ fontSize: 11.5, color: T.soft, ...mono }}>{shown === total ? `${total} ${t("workforce.catalogue.services", "services")}` : `${shown} / ${total}`}</span>
                {config ? (
                  <Btn size="sm" testid="wfcat-add" onClick={() => setEditing("new")}>
                    {t("workforce.catalogue.addService", "Add a service")}
                  </Btn>
                ) : null}
                <ViewToggle mode={view} onChange={setView} testid="wfcat-view" />
                {config && safe.branches.length ? (
                  /* Branch scope is a SEARCHABLE dropdown, not a pill list (operator, 2026-08-03):
                     a bank has far more branches than fit a row of chips. */
                  <span style={{ marginLeft: "auto" }}>
                    <BranchPicker branches={safe.branches} value={scope} onChange={setScope} />
                  </span>
                ) : null}
              </div>

              {/* Folder cards — phone/tablet stand-in for the tree: the current level's folders. */}
              {!desktop ? (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", flexShrink: 0 }}>
                  {(intentSel ? [] : lob ? safe.intents.filter((i) => i.lob === lob) : safe.lobs).map((f) => (
                    <button key={f.id} type="button" data-testid="wfcat-folder-card" data-id={f.id} onClick={() => setFscope(f.id)}
                      style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 12px", borderRadius: 10, border: `1px solid ${T.border}`, background: T.white, color: T.n1, fontSize: 13, fontWeight: 600, fontFamily: T.font, cursor: "pointer" }}>
                      <Icon.folder size={14} />{f.name}
                    </button>
                  ))}
                </div>
              ) : null}

              {/* Breadcrumb — same shape as the shared folder breadcrumb: hidden at the top level,
                  link crumbs back out, current folder bold. */}
              {fscope !== "all" ? (
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: T.soft, flexShrink: 0 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <Btn variant="link" size="sm" testid="wfcat-crumb-all" onClick={() => setFscope("all")} style={{ padding: 0, height: "auto" }}>
                      {`${t("workforce.catalogue.allOf", "All")} ${v.lobsLower}`}
                    </Btn>
                  </span>
                  {lob ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <Icon.arrow dir="right" size={10} />
                      {intentSel
                        ? <Btn variant="link" size="sm" testid="wfcat-crumb-lob" onClick={() => setFscope(lob)} style={{ padding: 0, height: "auto" }}>{lobName(lob)}</Btn>
                        : <span style={{ color: T.n1, fontWeight: 700 }}>{lobName(lob)}</span>}
                    </span>
                  ) : null}
                  {intentSel ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <Icon.arrow dir="right" size={10} />
                      <span style={{ color: T.n1, fontWeight: 700 }}>{intentName(intentSel)}</span>
                    </span>
                  ) : null}
                </div>
              ) : null}

              {groups.length === 0 ? (
                <div style={{ padding: 32, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("workforce.catalogue.nothingMatches", "Nothing matches that search.")}</div>
              ) : view === "grid" ? (
                <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 14 }} data-testid="wfcat-card-grid">
                  {flatServices.map(({ s, intent: i, lob: l }) => (
                    <div key={s.id} data-testid="wfcat-service-card" data-id={s.id} onClick={() => (config ? setEditing(s) : setDetail(s))}
                      style={{ ...card, cursor: "pointer", display: "flex", flexDirection: "column", opacity: s.archived || s.bookableNow === false ? 0.5 : 1 }}>
                      {s.imageUrl ? <img src={s.imageUrl} alt="" style={{ width: "100%", height: 104, objectFit: "cover", background: T.n6 }} /> : null}
                      <div style={{ padding: "13px 16px 15px", display: "flex", flexDirection: "column", gap: 9, flex: 1 }}>
                        <span style={{ ...cap, fontSize: 10 }}>{l.name} · {i.name}</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>
                          {s.name}
                          {s.archived ? <span style={{ fontSize: 10.5, color: T.soft, fontWeight: 600 }}> · {t("workforce.catalogue.archived", "Archived")}</span> : null}
                        </span>
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <BlockBar s={s} />
                          <span style={{ fontSize: 11.5, color: T.soft, ...mono }}>{t("common.units.minutesCompact", "{n}m").replace("{n}", String(s.blockMin))}</span>
                        </span>
                        <span style={{ fontSize: 11.5, color: T.n2 }}>{channelLabel(s.channels, t, v.site)}</span>
                        <span style={{ marginTop: "auto", paddingTop: 4 }}>
                          {s.minAdvance
                            ? <span style={{ fontSize: 11, color: T.soft, ...mono }}>{s.minAdvance}</span>
                            : <Pill tone="neutral">{t("workforce.catalogue.notSet", "not set")}</Pill>}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : groups.map((g) => (
                <div key={g.lob.id} style={card} data-testid="wfcat-lob-group" data-id={g.lob.id}>
                  <div style={{ padding: "13px 20px", borderBottom: `1px solid ${T.n6}`, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", background: alpha(T.n1, 2), flexWrap: "wrap" }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{g.lob.name}</span>
                    <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {config ? (
                        newIntentFor === g.lob.id ? (
                          <>
                            <input autoFocus value={newIntent} onChange={(e) => setNewIntent(e.target.value)} data-testid="wfcat-new-intent"
                              placeholder={t("workforce.catalogue.newIntentPlaceholder", "New intent name…")}
                              style={{ height: 28, width: 190, padding: "0 9px", borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: T.font, color: T.n1 }} />
                            <Btn size="sm" disabled={!newIntent.trim() || busy !== null} testid="wfcat-add-intent" onClick={() => addIntent(g.lob.id)}>
                              {busy === "tree" ? "…" : t("workforce.catalogue.add", "Add")}
                            </Btn>
                            <Btn size="sm" variant="ghost" onClick={() => { setNewIntentFor(null); setNewIntent(""); }}>{t("common.actions.cancel", "Cancel")}</Btn>
                          </>
                        ) : (
                          <button type="button" data-testid="wfcat-new-intent-open" onClick={() => { setNewIntentFor(g.lob.id); setNewIntent(""); }}
                            style={{ border: "none", background: "none", padding: 0, cursor: "pointer", color: T.p1, fontSize: 11.5, fontWeight: 700 }}>
                            + {t("workforce.catalogue.addIntent", "Add intent")}
                          </button>
                        )
                      ) : null}
                      <span style={{ ...cap, fontSize: 10.5 }}>{g.count} {t("workforce.catalogue.services", "services")}</span>
                    </span>
                  </div>
                  {g.intents.map((ig) => (
                    <div key={ig.intent.id}>
                      <div style={{ padding: "9px 20px", background: T.white, borderBottom: `1px solid ${T.n6}`, display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                        <span style={{ ...cap, fontSize: 10 }}>{safe.intentLabel || "Intent"} · {ig.intent.name}</span>
                      </div>
                      {ig.services.map((s) => (
                        <div key={s.id} data-testid="wfcat-service-row" data-id={s.id} onClick={() => (config ? setEditing(s) : setDetail(s))}
                          style={{ display: "grid", gridTemplateColumns: "minmax(180px,1.6fr) 86px 118px minmax(120px,1fr) 96px", gap: 12, alignItems: "center", padding: "12px 20px", borderBottom: `1px solid ${T.n6}`, cursor: "pointer", opacity: s.archived || s.bookableNow === false ? 0.5 : 1 }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                            {s.imageUrl ? (
                              <img src={s.imageUrl} alt="" data-testid="wfcat-row-thumb"
                                style={{ width: 40, height: 24, objectFit: "cover", borderRadius: 4, flexShrink: 0, background: T.n6 }} />
                            ) : null}
                          <span style={{ fontSize: 13, fontWeight: 600, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {s.name}
                            {s.archived ? <span style={{ fontSize: 10.5, color: T.soft, fontWeight: 600 }}> · {t("workforce.catalogue.archived", "Archived")}</span> : null}
                            {!s.archived && s.unavailableReason === "before-window" ? <span style={{ fontSize: 10.5, color: T.orange1, fontWeight: 700 }}> · {t("workforce.catalogue.fromDate", "from")} {s.disableBefore}</span> : null}
                            {!s.archived && s.unavailableReason === "after-window" ? <span style={{ fontSize: 10.5, color: T.orange1, fontWeight: 700 }}> · {t("workforce.catalogue.endedDate", "ended")} {s.disableAfter}</span> : null}
                            {s.id.startsWith("sw_svc_new_") ? <span style={{ fontSize: 10.5, color: T.p1, fontWeight: 700 }}> · {t("workforce.catalogue.added", "Added")}</span> : null}
                            {s.modules && !s.modules.appointments && (s.modules.queueing || s.modules.events) ? (
                              <span style={{ fontSize: 10.5, color: T.soft, fontWeight: 600 }}> · {s.modules.queueing ? t("workforce.catalogue.modQueueing", "Queueing") : t("workforce.catalogue.modEvents", "Events")}</span>
                            ) : null}
                          </span>
                          </span>
                          <span style={{ fontSize: 12.5, color: T.n1, textAlign: "right", ...mono }}>{t("common.units.minutesCompact", "{n}m").replace("{n}", String(s.durationMin))}</span>
                          <span style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
                            <BlockBar s={s} />
                            <span style={{ fontSize: 11.5, color: T.soft, ...mono }}>{t("common.units.minutesCompact", "{n}m").replace("{n}", String(s.blockMin))}</span>
                          </span>
                          <span style={{ fontSize: 11.5, color: T.n2 }}>{channelLabel(s.channels, t, v.site)}</span>
                          <span style={{ display: "flex", justifyContent: "flex-end" }}>
                            {s.minAdvance
                              ? <span style={{ fontSize: 11, color: T.soft, ...mono }}>{s.minAdvance}</span>
                              : <Pill tone="neutral">{t("workforce.catalogue.notSet", "not set")}</Pill>}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
              {!config ? (
                <span style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.55 }}>
                  {t("workforce.catalogue.viewOnlyNote", "This catalog is maintained by Operations. Tap a service to see its length, channels and booking rules.")}
                </span>
              ) : null}
              </div>
            </div>
          ) : tab === "import" ? (
            <>
              {/* 1 — the template. Offered first: the operator's file is usually built days before
                  they open this screen, so the column contract must be gettable up front. */}
              <div style={{ ...card, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
                <span style={cap}>{t("workforce.catalogue.startWithTheTemplate", "Start with the template")}</span>
                <span style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.55 }}>
                  {t("workforce.catalogue.templateBody", "The template carries every column, one filled example row, and notes explaining each field. Lines beginning with # are ignored on import, so the notes can stay while you work.")}
                </span>
                <div style={{ display: "flex", gap: 9, flexWrap: "wrap", alignItems: "center" }}>
                  <Btn size="sm" variant="secondary" testid="wfcat-import-template"
                    onClick={() => downloadText("services-import-template.csv", importTemplateCsv())}>
                    {t("workforce.catalogue.downloadTemplate", "Download template")}
                  </Btn>
                  <span style={{ fontSize: 11.5, color: T.soft }}>
                    {IMPORT_COLUMNS.length} {t("workforce.catalogue.columns", "columns")} · {IMPORT_COLUMNS.filter((c) => c.required).length} {t("workforce.catalogue.required", "required")}
                  </span>
                </div>
              </div>

              {/* 2 — the upload. Header problems are reported BEFORE anything is sent: a wrong
                  column map is a file problem, not a per-row problem. */}
              <div style={{ ...card, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
                <span style={cap}>{t("workforce.catalogue.uploadYourFile", "Upload your file")}</span>
                <label data-testid="wfcat-import-drop"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); stageFile(e.dataTransfer.files?.[0] ?? null); }}
                  style={{ position: "relative", padding: "22px 18px", borderRadius: 10, border: `1px dashed ${T.n4}`, background: alpha(T.n1, 2), cursor: "pointer", textAlign: "center" }}>
                  <span style={{ fontSize: 12.5, color: T.n2 }}>{t("workforce.catalogue.dropCsv", "Drop a CSV here, or click to choose a file")}</span>
                  <input type="file" accept=".csv,text/csv" onChange={(e) => stageFile(e.target.files?.[0] ?? null)}
                    style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }} />
                </label>
                {staged ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 11, padding: "13px 15px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.white }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{staged.fileName}</span>
                      <span style={{ fontSize: 11.5, color: T.soft, ...mono }}>{staged.rows.length} {t("workforce.catalogue.rowsFound", "rows found")}</span>
                    </div>

                    {staged.check.validRows.length > MAX_IMPORT_ROWS ? (
                      <span style={{ fontSize: 12, color: T.red1, fontWeight: 600, lineHeight: 1.5 }}>
                        {t("workforce.catalogue.tooManyRows", "One import carries at most")} {MAX_IMPORT_ROWS} {t("workforce.catalogue.tooManyRowsTail", "services — this file has")} {staged.check.validRows.length}. {t("workforce.catalogue.tooManyRowsFix", "Split it into smaller files and import them one after another.")}
                      </span>
                    ) : null}

                    {/* File-level problems first: a wrong column map is not a per-row problem. */}
                    {staged.missingRequired.length ? (
                      <span style={{ fontSize: 12, color: T.red1, fontWeight: 600, lineHeight: 1.5 }}>
                        {t("workforce.catalogue.missingColumns", "Missing required columns:")} {staged.missingRequired.join(", ")}
                      </span>
                    ) : null}
                    {staged.unknownHeaders.length ? (
                      <span style={{ fontSize: 11.5, color: T.orange1, lineHeight: 1.5 }}>
                        {t("workforce.catalogue.ignoredColumns", "These columns aren't part of the template and will be ignored:")} {staged.unknownHeaders.join(", ")}
                      </span>
                    ) : null}

                    {/* Row-level check, run before anything is sent. */}
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <Pill tone={staged.check.validRows.length ? "success" : "danger"} dot>
                        {staged.check.validRows.length} {t("workforce.catalogue.readyToImport", "ready to import")}
                      </Pill>
                      {staged.check.invalidLines.length ? (
                        <Pill tone="warn" dot>{staged.check.invalidLines.length} {t("workforce.catalogue.withProblems", "with problems")}</Pill>
                      ) : (
                        <span style={{ fontSize: 11.5, color: T.green1, fontWeight: 600 }}>{t("workforce.catalogue.allRowsValid", "Every row passed the checks.")}</span>
                      )}
                    </div>

                    {staged.check.issues.length ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "11px 13px", borderRadius: 8, background: alpha(T.orange1, 6), border: `1px solid ${alpha(T.orange1, 26)}`, maxHeight: 190, overflow: "auto" }}>
                        {staged.check.issues.slice(0, 24).map((iss, ii) => (
                          <span key={ii} style={{ fontSize: 11.5, color: T.n2, lineHeight: 1.5 }}>
                            <span style={{ ...mono, color: T.soft }}>{t("workforce.catalogue.lineShort", "Line")} {iss.line}</span>
                            {" — "}<b style={{ fontWeight: 600 }}>{iss.field}</b>{": "}{iss.problem}
                          </span>
                        ))}
                        {staged.check.issues.length > 24 ? (
                          <span style={{ fontSize: 11, color: T.soft }}>+{staged.check.issues.length - 24} {t("workforce.catalogue.moreProblems", "more — download the rows with issues for the full list")}</span>
                        ) : null}
                      </div>
                    ) : null}

                    {/* Two downloads (operator, 2026-08-04): the whole file back, or just the rows
                        that need fixing. The second is a real CSV with the original columns, so it
                        can be corrected and re-imported directly. */}
                    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
                      <button type="button" data-testid="wfcat-staged-download-all" onClick={() => downloadText(staged.fileName, staged.fileText)}
                        style={{ border: "none", background: "none", padding: 0, cursor: "pointer", color: T.p1, fontSize: 11.5, fontWeight: 700 }}>
                        {t("workforce.catalogue.downloadWholeFile", "Download the whole file")}
                      </button>
                      {staged.check.invalidLines.length ? (
                        <button type="button" data-testid="wfcat-staged-download-issues"
                          onClick={() => downloadText(`rows-with-issues-${staged.fileName}`, issueRowsCsv(staged.fileText, staged.check.issues))}
                          style={{ border: "none", background: "none", padding: 0, cursor: "pointer", color: T.p1, fontSize: 11.5, fontWeight: 700 }}>
                          {t("workforce.catalogue.downloadIssueRows", "Download just the rows with issues")}
                        </button>
                      ) : null}
                    </div>

                    <div style={{ display: "flex", gap: 9, flexWrap: "wrap", alignItems: "center" }}>
                      <Btn size="sm" testid="wfcat-import-run"
                        disabled={busy !== null || !staged.check.validRows.length || staged.missingRequired.length > 0 || staged.check.validRows.length > MAX_IMPORT_ROWS}
                        onClick={doImport}>
                        {busy === "import" ? t("workforce.catalogue.importing", "Importing…") : `${t("workforce.catalogue.importRows", "Import")} ${staged.check.validRows.length}`}
                      </Btn>
                      <Btn size="sm" variant="secondary" onClick={() => setStaged(null)}>{t("common.actions.cancel", "Cancel")}</Btn>
                      <span style={{ fontSize: 11, color: T.soft, lineHeight: 1.5 }}>
                        {staged.check.invalidLines.length
                          ? t("workforce.catalogue.skipNote", "Rows with problems are left out — fix them and import again.")
                          : t("workforce.catalogue.perRowNote", "Rows are checked one at a time — good rows are created even if others fail.")}
                      </span>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* 3 — the history, as a COLUMN TABLE (operator, 2026-08-04): Status · Import name ·
                  Upload date/time · Completion date/time, with the download to the right of the
                  completion date. Same content as before, read as rows: two timestamps rather than
                  one, because "handed over" and "finished" answer different questions. Failures
                  expand under their own row, so the table stays scannable when nothing went wrong. */}
              <div style={card}>
                <div style={{ padding: "13px 20px", borderBottom: `1px solid ${T.n6}` }}>
                  <span style={cap}>{t("workforce.catalogue.importHistory", "Import history")}</span>
                </div>
                {!(data?.imports ?? []).length ? (
                  <div style={{ padding: 28, textAlign: "center", fontSize: 13, color: T.soft }}>
                    {t("workforce.catalogue.noImportsYet", "No imports yet. Every run is recorded here with who ran it and what happened to each row.")}
                  </div>
                ) : (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: IMPORT_COLS, gap: 12, padding: "9px 20px", borderBottom: `1px solid ${T.n6}`, background: alpha(T.n1, 2) }}>
                      {[
                        t("workforce.catalogue.colStatus", "Status"),
                        t("workforce.catalogue.colImportName", "Import name"),
                        t("workforce.catalogue.colUploaded", "Upload date/time"),
                        t("workforce.catalogue.colCompleted", "Completion date/time"),
                        "",
                      ].map((h, hi) => <span key={hi} style={{ ...cap, fontSize: 10 }}>{h}</span>)}
                    </div>
                    {(data?.imports ?? []).map((runRow, ri) => {
                      const failed = runRow.failed > 0;
                      const status = runRow.status ?? (failed ? (runRow.imported ? "partial" : "failed") : "completed");
                      const open = openRun === runRow.id;
                      const last = ri === (data?.imports ?? []).length - 1;
                      return (
                        <div key={runRow.id} data-testid="wfcat-import-run" data-id={runRow.id}
                          style={{ borderBottom: last && !open ? "none" : `1px solid ${T.n6}` }}>
                          <div style={{ display: "grid", gridTemplateColumns: IMPORT_COLS, gap: 12, padding: "13px 20px", alignItems: "center" }}>
                            <span>
                              <Pill tone={status === "completed" ? "success" : status === "failed" ? "danger" : "warn"} dot>
                                {status === "completed" ? t("workforce.catalogue.statusCompleted", "Completed")
                                  : status === "failed" ? t("workforce.catalogue.statusFailed", "Failed")
                                  : t("workforce.catalogue.statusPartial", "Completed with errors")}
                              </Pill>
                            </span>
                            <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{runRow.fileName}</span>
                              <span style={{ fontSize: 11, color: T.soft }}>
                                {runRow.imported}/{runRow.rowCount} {t("workforce.catalogue.importedOf", "imported")}
                                {failed ? (
                                  <>
                                    {" · "}
                                    <button type="button" data-testid="wfcat-import-expand" onClick={() => setOpenRun(open ? null : runRow.id)}
                                      style={{ border: "none", background: "none", padding: 0, cursor: "pointer", color: T.red1, fontSize: 11, fontWeight: 700 }}>
                                      {runRow.failed} {open ? t("workforce.catalogue.hideFailed", "failed — hide") : t("workforce.catalogue.showFailed", "failed — show")}
                                    </button>
                                  </>
                                ) : null}
                              </span>
                            </span>
                            <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                              <span style={{ fontSize: 12.5, color: T.n1, ...mono }}>{new Date(runRow.uploadedAt ?? runRow.at).toLocaleString()}</span>
                              <span style={{ fontSize: 11, color: T.soft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{runRow.by}{runRow.byRole ? ` · ${runRow.byRole}` : ""}</span>
                            </span>
                            <span style={{ fontSize: 12.5, color: T.n1, ...mono }}>{new Date(runRow.completedAt ?? runRow.at).toLocaleString()}</span>
                            {/* Two downloads per run (operator, 2026-08-04): the whole file, or just
                                the rows that failed. The failed-rows file is re-importable. */}
                            <span style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
                              {runRow.fileText ? (
                                <Btn size="sm" variant="secondary" testid="wfcat-import-download"
                                  onClick={() => downloadText(runRow.fileName, runRow.fileText || "")}>
                                  {t("workforce.catalogue.downloadFile", "Whole file")}
                                </Btn>
                              ) : null}
                              {failed ? (
                                <button type="button" data-testid="wfcat-import-download-issues"
                                  onClick={() => downloadText(`rows-with-issues-${runRow.fileName}`,
                                    runRow.fileText
                                      ? issueRowsCsv(runRow.fileText, runRow.results.filter((x) => !x.ok).map((x) => ({ line: x.line, field: x.name, problem: x.reason || "" })))
                                      : failureReportCsv(runRow.fileName, runRow.results))}
                                  style={{ border: "none", background: "none", padding: 0, cursor: "pointer", color: T.p1, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
                                  {t("workforce.catalogue.failedRowsOnly", "Failed rows only")}
                                </button>
                              ) : null}
                            </span>
                          </div>
                          {open && failed ? (
                            <div style={{ padding: "0 20px 14px", display: "flex", flexDirection: "column", gap: 9 }}>
                              <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "11px 13px", borderRadius: 8, background: alpha(T.red1, 5), border: `1px solid ${alpha(T.red1, 22)}` }}>
                                {runRow.results.filter((x) => !x.ok).map((x) => (
                                  <span key={x.line} style={{ fontSize: 11.5, color: T.n2, lineHeight: 1.5 }}>
                                    <span style={{ ...mono, color: T.soft }}>{t("workforce.catalogue.lineShort", "Line")} {x.line}</span>
                                    {" — "}<b style={{ fontWeight: 600 }}>{x.name}</b>{": "}{x.reason}
                                  </span>
                                ))}
                              </div>
                              <button type="button" data-testid="wfcat-import-failreport"
                                onClick={() => downloadText(`failed-rows-${runRow.fileName}`, failureReportCsv(runRow.fileName, runRow.results))}
                                style={{ alignSelf: "flex-start", border: "none", background: "none", padding: 0, cursor: "pointer", color: T.p1, fontSize: 11.5, fontWeight: 700 }}>
                                {t("workforce.catalogue.downloadFailures", "Download the failed rows")}
                              </button>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </>
          ) : (
            <div style={card}>
              <div style={{ padding: "13px 20px", borderBottom: `1px solid ${T.n6}` }}>
                <span style={cap}>{`${t("workforce.catalogue.which", "Which")} ${v.sitesLower} ${t("workforce.catalogue.runEach", "run each")} ${v.lobLower}`}</span>
              </div>
              {safe.lobs.map((l) => {
                const on = new Set(safe.branches.filter((b) => (safe.branchLobs[b] ?? []).includes(l.id)));
                return (
                  <LobBranchPicker key={l.id} lob={l} branches={safe.branches} selected={on}
                    busy={busy === `bl:${l.id}`}
                    onToggle={(b, next) => toggleBranchLob(b, l.id, next)}
                    onAll={() => safe.branches.filter((b) => !on.has(b)).forEach((b) => toggleBranchLob(b, l.id, true))}
                    onNone={() => [...on].forEach((b) => toggleBranchLob(b, l.id, false))} />
                );
              })}
              <div style={{ padding: "14px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
                <span style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.55 }}>
                  {`${t("workforce.catalogue.noteLead", "Switching one off at a")} ${v.siteLower} ${t("workforce.catalogue.noteMid", "removes its services from that")} ${v.siteLower}${t("workforce.catalogue.noteTail", "'s booking surface. Individual services can be switched off from the")} ${v.siteLower} ${t("workforce.catalogue.noteEnd", "view of the catalogue.")}`}
                </span>
                {/* Riley extends the tree here too — a new line of business starts life offered
                    everywhere, the same default as the pickers above. */}
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", paddingTop: 2 }}>
                  <input value={newLob} onChange={(e) => setNewLob(e.target.value)} data-testid="wfcat-new-lob"
                    placeholder={`${t("workforce.catalogue.newWord", "New")} ${v.lobLower}…`}
                    style={{ height: 32, width: 236, padding: "0 10px", borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12.5, fontFamily: T.font, color: T.n1 }} />
                  <Btn size="sm" variant="secondary" disabled={!newLob.trim() || busy !== null} testid="wfcat-add-lob" onClick={addLob}>
                    {busy === "tree" ? t("workforce.catalogue.saving", "Saving…") : `${t("workforce.catalogue.addWord", "Add")} ${v.lobLower}`}
                  </Btn>
                </div>
              </div>
            </div>
          )}
      </div>
      {detail ? <ServiceDetail svc={detail} intentName={intentName(detail.intent)} lobName={lobName(detail.lob)} groupName={groupName} surveyName={surveyName} couponName={couponName} onClose={() => setDetail(null)} /> : null}
      {config && editing && safe ? (
        <ServiceEditor svc={editing === "new" ? null : editing}
          catalogue={{ lobs: safe.lobs, intents: safe.intents, questionGroups: safe.questionGroups, surveys: safe.surveys, coupons: safe.coupons }}
          busy={busy} onSave={saveService} onArchive={archiveService} onRestore={restoreService}
          onDelete={(s) => setConfirmDelete(s)} onClose={() => setEditing(null)} />
      ) : null}
      {/* Delete asks first, and says what it cannot touch (operator ruling: seeded rows are never
          removed — the engine refuses them 409). */}
      {confirmDelete ? (
        <Modal testid="wfcat-delete-modal" tone="danger" onClose={() => setConfirmDelete(null)}
          title={t("workforce.catalogue.deleteTitle", "Delete this service?")}
          sub={confirmDelete.name}
          footer={
            <>
              <Btn variant="secondary" onClick={() => setConfirmDelete(null)}>{t("common.actions.cancel", "Cancel")}</Btn>
              <Btn variant="danger" disabled={busy !== null} testid="wfcat-delete-confirm" onClick={() => deleteService(confirmDelete)}>
                {busy === "save" ? t("workforce.catalogue.deleting", "Deleting…") : t("workforce.catalogue.deleteConfirm", "Delete service")}
              </Btn>
            </>
          }>
          <p style={{ fontSize: 13, color: T.n2, lineHeight: 1.55, margin: 0 }}>
            {t("workforce.catalogue.deleteBody", "It disappears from the catalog and from every booking surface. Appointments already booked against it keep their record, but nobody can book it again.")}
          </p>
          <p style={{ fontSize: 12, color: T.soft, lineHeight: 1.55, margin: "9px 0 0" }}>
            {t("workforce.catalogue.deleteUndo", "This cannot be undone. To retire a service temporarily, archive it instead.")}
          </p>
        </Modal>
      ) : null}
    </Screen>
  );
}
